/**
 * Payments & Dues helpers.
 * Each receivable is an independent sale — no customer ledger or allocation.
 */

export const RECEIVABLE_STATUS = {
  UNPAID: 'unpaid',
  PARTIAL: 'partial',
  PAID: 'paid'
};

export const OPEN_STATUSES = [RECEIVABLE_STATUS.UNPAID, RECEIVABLE_STATUS.PARTIAL];

export const RECEIVABLE_SOURCE = {
  ORDER: 'order',
  MANUAL: 'manual'
};

export const ELIGIBLE_ORDER_STATUSES = ['READY_TO_DISPATCH', 'DISPATCHED'];

export const emptyReminders = () => ({
  d5: false,
  d2: false,
  d0: false,
  od3: false,
  od5: false,
  od7: false,
  od10: false
});

export const roundMoney = (value) => {
  const numeric = Number(value);
  if (!isFinite(numeric)) return 0;
  return Math.round(numeric * 100) / 100;
};

export const formatMoney = (value) => {
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(roundMoney(value))}`;
};

export const toDate = (value) => {
  if (!value) return null;

  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return isNaN(converted.getTime()) ? null : converted;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const converted = new Date(value);
  return isNaN(converted.getTime()) ? null : converted;
};

export const startOfDay = (value) => {
  const date = toDate(value);
  if (!date) return null;
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

export const addDays = (value, days) => {
  const date = startOfDay(value);
  if (!date) return null;
  const copy = new Date(date);
  copy.setDate(copy.getDate() + Number(days || 0));
  return copy;
};

export const toDateInputValue = (value) => {
  const date = startOfDay(value);
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const parseDateInput = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  return isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const daysUntil = (value) => {
  const target = startOfDay(value);
  if (!target) return null;
  const today = startOfDay(new Date());
  return Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

export const computeStatus = (invoiceAmount, amountReceived) => {
  const outstanding = roundMoney(roundMoney(invoiceAmount) - roundMoney(amountReceived));
  if (outstanding <= 0) return RECEIVABLE_STATUS.PAID;
  if (roundMoney(amountReceived) > 0) return RECEIVABLE_STATUS.PARTIAL;
  return RECEIVABLE_STATUS.UNPAID;
};

export const cleanCustomerName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name.trim().replace(/\s+/g, ' ');
};
